/**
 * A Mate's key reaches its own project.
 *
 * Every Mate holds one integration token, the `ZCP_API_KEY` of its `zcp`
 * container: `NO_ACCESS` at the organization and `BASIC_USER` on the project
 * it lives in, and no grant on any other project. The agent in that container
 * reads and changes its own project through it, and nothing else.
 *
 * It once read its application's other projects too, `READ_ONLY` on each, so
 * an agent could see its stage and production. That grant reads a
 * production's unmarked secrets — a database's `connectionString` in plain
 * text — for anybody with a terminal in the Mate, and somebody had to keep
 * writing it as the application changed (ADR 0003). An agent reaches past its
 * project through HQ instead: zerops_observe reads permitted stage and
 * production status, active versions and logs. So this client adds no grant beside a
 * Mate's own: not at the key's mint, not at a birth, not on a screen's read.
 * A key known for the Mate's by the id HQ holds loses any other grant at its
 * harden; a key found by its name alone is the Mate's only while it holds its
 * own project and nothing else, and is never narrowed (`planMateKey`).
 *
 * What stays is the lowering (guide 0.2). The platform mints a container's
 * token with `ADMIN` on its project; a key this client mints holds
 * `BASIC_USER` from the start, and one it did not mint — the pool's from
 * sign-up, an older account's, one made in the Zerops GUI — is lowered in
 * place by its harden (`hardenMate`), when a person finishes setting it up,
 * never on a screen's read (step A, A11): `PUT
 * /client/{clientId}/integration-token/{tokenId}` rewrites the grants of the
 * same token string the container holds, with no restart.
 *
 * Nothing here reaches a network (rule R1): the caller performs the write.
 *
 * @module groupReach
 */
import { mateKeyReach } from "@t3tools/shared/mateKeyReach";

/** The platform's project roles, as its own validation error enumerates them. */
export type ZeropsProjectRole = "OWNER" | "ADMIN" | "BASIC_USER" | "READ_ONLY" | "NO_ACCESS";

/**
 * The names a Mate's key comes with: a press's, `zcp-<project>`, as the platform named the one it
 * minted with a container; and the Zerops GUI's, `zerops-zcp-<service>` (the audit, 2026-10-03:
 * 63 `zerops-zcp-zcp` keys on Example Org, none of which `zcp-` matched).
 */
const MATE_KEY_NAME_PREFIXES: ReadonlyArray<string> = ["zcp-", "zerops-zcp-"];

/**
 * What a Mate holds on the project it lives in — everything zcp's bootstrap
 * does and nothing more.
 *
 * The platform mints the container's token with `ADMIN` (measured
 * 2026-09-15), which is also a shell in that container and the agent running
 * in it: `ADMIN` rewrites the project's own tags and renames the project — the
 * name every surface calls the Mate by. `BASIC_USER` keeps the whole bootstrap
 * — service import with `override`, plain and sensitive service env, restart,
 * delete, `zcli push` — and answers `403` to the tag and rename writes
 * (measured 2026-09-15, ledger *Zerops auth surface*). Where a Mate belongs is
 * HQ's record, which no key of a Mate writes; the lowering keeps the agent to
 * the work in its own project.
 */
export const MATE_SELF_PROJECT_ROLE = "BASIC_USER" satisfies ZeropsProjectRole;

export interface ZeropsProjectGrant {
  readonly projectId: string;
  readonly roleCode: ZeropsProjectRole;
}

export interface ZeropsIntegrationToken {
  readonly id: string;
  readonly name: string;
  /** The token's org role, round-tripped by any write to it. */
  readonly roleCode?: string | undefined;
  readonly projects?: ReadonlyArray<ZeropsProjectGrant> | undefined;
  /** When the platform minted it. The start-up throwaway sweep dates rows by it. */
  readonly created?: string | undefined;
  /** Who minted it: besides an org owner, the one person who may write it. */
  readonly createdByUser?: string | undefined;
}

/**
 * A one-time permission to mint one token of an exact shape, granted by a
 * person and attached to a token.
 *
 * Environment creation leaves one on every Mate: the platform's
 * development-container import grants `NO_ACCESS` + *can create projects*
 * (measured 2026-09-15), so every Mate on the account can make itself one more
 * project — and, worse for the door, a token whose `createdByUser` is the
 * person who granted it. Nothing zcp does needs it: its delegated launch path
 * falls back to a manual key.
 */
export interface ZeropsTokenDelegation {
  readonly id: string;
  readonly tokenId: string;
}

/**
 * Whether `token` is a key of the Mate whose own project is `projectId`: one of the names a
 * Mate's key comes with, and its only grant this project, at a Mate's own role, either one.
 *
 * Both halves of the test are needed. The grant alone is not enough — a deploy key, or a person's
 * own token scoped to the project, looks identical by it; the name alone is not either. A key
 * widened beyond its project is not taken for one: the harden finds the key its Mate holds by its
 * id once the Mate enrolled it with HQ, and by this rule only until then.
 */
function isMateKeyOf(token: ZeropsIntegrationToken, projectId: string): boolean {
  return (
    MATE_KEY_NAME_PREFIXES.some((prefix) => token.name.startsWith(prefix)) &&
    mateKeyReach(token.projects ?? [], projectId) === "own"
  );
}

const createdMs = (token: ZeropsIntegrationToken): number =>
  token.created === undefined ? Number.NaN : Date.parse(token.created);

/** Newest first; a key with no readable age last. */
function newestFirst(keys: ReadonlyArray<ZeropsIntegrationToken>): Array<ZeropsIntegrationToken> {
  return [...keys].sort((left, right) => {
    const l = createdMs(left);
    const r = createdMs(right);
    if (Number.isNaN(l)) return Number.isNaN(r) ? 0 : 1;
    if (Number.isNaN(r)) return -1;
    return r - l;
  });
}

/** Whether `key` was made before the container made at `container` (wall ms); any, where unknown. */
const madeBefore = (key: ZeropsIntegrationToken, container: number): boolean =>
  Number.isNaN(container) || createdMs(key) <= container;

/**
 * The key a Mate's container holds, out of every key that is its (`isMateKeyOf`): the newest made
 * before its container — the one there is, or the newest where a raced press or an older platform
 * key left two. A key made after it is an orphan; where the container's age is unknown, only a key
 * that is the one there is stands, and keys nothing tells apart are never guessed at: undefined.
 */
export function findHeldMateKey(
  tokens: ReadonlyArray<ZeropsIntegrationToken>,
  projectId: string,
  containerCreated: string | undefined,
): ZeropsIntegrationToken | undefined {
  const keys = tokens.filter((token) => isMateKeyOf(token, projectId));
  const container = containerCreated === undefined ? Number.NaN : Date.parse(containerCreated);
  if (Number.isNaN(container)) return keys.length === 1 ? keys[0] : undefined;
  return newestFirst(keys.filter((key) => madeBefore(key, container)))[0];
}

/** A key's role on its Mate's own project. */
function selfRoleOf(token: ZeropsIntegrationToken, projectId: string): string | undefined {
  return (token.projects ?? []).find((grant) => grant.projectId === projectId)?.roleCode;
}

/**
 * Every key of a Mate still `ADMIN` on its own project, made before its container where its age is
 * known: what a harden lowers.
 */
export function mateAdminKeys(
  tokens: ReadonlyArray<ZeropsIntegrationToken>,
  projectId: string,
  containerCreated: string | undefined,
): ReadonlyArray<ZeropsIntegrationToken> {
  const container = containerCreated === undefined ? Number.NaN : Date.parse(containerCreated);
  return tokens.filter(
    (token) =>
      isMateKeyOf(token, projectId) &&
      selfRoleOf(token, projectId) === "ADMIN" &&
      madeBefore(token, container),
  );
}

/** The name a press gives the key it mints with a Mate's container (`api.ts` `mateKeyName`). */
const PRESS_KEY_NAME_PREFIX = "zcp-";

/**
 * The key a press reuses where no container holds one yet: the newest of the keys a press mints,
 * by the name it gives them, holding this project alone at a Mate's own role — so a press never
 * mints a second key beside one it made. A key an earlier client widened to other projects is not
 * reused: it is not taken for the Mate's (`isMateKeyOf`), and a key of the press's own is minted.
 */
export function newestMateKey(
  tokens: ReadonlyArray<ZeropsIntegrationToken>,
  projectId: string,
): ZeropsIntegrationToken | undefined {
  return newestFirst(
    tokens.filter(
      (token) => token.name.startsWith(PRESS_KEY_NAME_PREFIX) && isMateKeyOf(token, projectId),
    ),
  )[0];
}

/** What a harden or a reusing press does with one key of a Mate (`planMateKey`). */
export type MateKeyPlan =
  /** Write exactly `projects`: the Mate's own project at `MATE_SELF_PROJECT_ROLE`, nothing else. */
  | {
      readonly kind: "write";
      readonly tokenId: string;
      readonly projects: ReadonlyArray<ZeropsProjectGrant>;
    }
  /** It holds exactly its own project at the Mate's role already: nothing is written. */
  | { readonly kind: "held" }
  /** Found by its name, it is not the Mate's key as read: nothing is written to it. */
  | { readonly kind: "not-its-key" };

/**
 * The write that sets a Mate's key to its own project alone (ADR 0003): `MATE_SELF_PROJECT_ROLE`
 * there and no other grant. A key the platform minted with `ADMIN` is lowered in place, its string
 * unchanged; a key that holds exactly that already is not written.
 *
 * How the key was found decides whether it may be narrowed. One found by the id the Mate enrolled
 * with HQ (`foundBy: "id"`) is the Mate's — HQ took it only while it named this project alone — so
 * any other grant it holds is removed. One found by its name and grant (`foundBy: "name"`) is the
 * Mate's only while it holds this project alone (`isMateKeyOf`): a deploy key or a person's token
 * can carry such a name, so a key that reaches anything else is `not-its-key` and never narrowed.
 */
export function planMateKey(input: {
  readonly token: ZeropsIntegrationToken;
  readonly selfProjectId: string;
  readonly foundBy: "id" | "name";
}): MateKeyPlan {
  if (input.foundBy === "name" && !isMateKeyOf(input.token, input.selfProjectId)) {
    return { kind: "not-its-key" };
  }
  if (
    (input.token.projects ?? []).length === 1 &&
    selfRoleOf(input.token, input.selfProjectId) === MATE_SELF_PROJECT_ROLE
  ) {
    return { kind: "held" };
  }
  return {
    kind: "write",
    tokenId: input.token.id,
    projects: [{ projectId: input.selfProjectId, roleCode: MATE_SELF_PROJECT_ROLE }],
  };
}

/** The page's exclusive locks (`navigator.locks`): `hold` runs once the lock is this tab's. */
export interface TokenWriteLocks {
  readonly request: <T>(name: string, hold: () => Promise<T>) => Promise<T>;
}

export const tokenWriteLockName = (tokenId: string): string => `mate:token:${tokenId}`;

/** Holds one token's read-then-write at a time, until `run` settles. */
export type TokenWriteHold = <T>(tokenId: string, run: () => Promise<T>) => Promise<T>;

/** How long one token's read-then-write may hold its lock before it is let go and fails. */
export const TOKEN_WRITE_HOLD_MS = 30_000;

/**
 * One token's read-then-write at a time: in this page by a queue per token, and across the
 * browser's tabs by the page's lock named `mate:token:{id}` where the platform has locks. A hold
 * past `timeoutMs` is let go and fails, so a write that never answers blocks nobody for good.
 */
export function makeTokenWriteLock(
  locks?: TokenWriteLocks,
  options: { readonly timeoutMs?: number } = {},
): TokenWriteHold {
  const timeoutMs = options.timeoutMs ?? TOKEN_WRITE_HOLD_MS;
  const queues = new Map<string, Promise<void>>();
  return <T>(tokenId: string, run: () => Promise<T>): Promise<T> => {
    const bounded = () =>
      new Promise<T>((resolve, reject) => {
        // @effect-diagnostics-next-line globalTimers:off -- a plain Promise bound, outside any Effect.
        const timer = setTimeout(
          () => reject(new Error(`The write to token ${tokenId} took too long; it was let go.`)),
          timeoutMs,
        );
        run().then(
          (value) => {
            clearTimeout(timer);
            resolve(value);
          },
          (cause: unknown) => {
            clearTimeout(timer);
            reject(cause);
          },
        );
      });
    const held = () =>
      locks === undefined ? bounded() : locks.request(tokenWriteLockName(tokenId), bounded);
    const next = (queues.get(tokenId) ?? Promise.resolve()).then(held);
    const settled = next.then(
      () => undefined,
      () => undefined,
    );
    queues.set(tokenId, settled);
    void settled.then(() => {
      if (queues.get(tokenId) === settled) queues.delete(tokenId);
    });
    return next;
  };
}
